import type { AddressStruct } from '../../../../../../types/common';

export type AddressAny = AddressStruct & { geoLat?: any; geoLon?: any };

export const emptyAddress: AddressAny = {
    postalCode: '',
    region: '',
    city: '',
    cityType: '',
    dpdCityId: null,
    street: '',
    streetType: '',
    streetId: null,
    house: '',
    block: '',
    flat: '',
};

const STREET_TYPE_MAP: Record<string, string> = {
    улица: 'ул.',
    ул: 'ул.',
    'ул.': 'ул.',
    проспект: 'пр-кт',
    пр: 'пр-кт',
    'пр-кт': 'пр-кт',
    переулок: 'пер.',
    пер: 'пер.',
    'пер.': 'пер.',
    бульвар: 'бул.',
    бул: 'бул.',
    'бул.': 'бул.',
    проезд: 'проезд',
    площадь: 'пл.',
    пл: 'пл.',
    'пл.': 'пл.',
    наб: 'наб.',
    набережная: 'наб.',
};

export function normalizeStreetType(t = '') {
    const key = t.replace(/\./g, '').toLowerCase();
    const found = Object.keys(STREET_TYPE_MAP).find((k) => k.replace(/\./g, '') === key);
    return found ? STREET_TYPE_MAP[found] : t;
}

export function buildFullAddress(a: AddressAny): string {
    const parts: string[] = [];
    if (/^\d{6}$/.test(a.postalCode)) parts.push(a.postalCode);

    if (a.city) {
        const cityType = a.cityType ? a.cityType.replace(/\.$/, '') + '.' : 'г.';
        parts.push(`${cityType} ${a.city}`.trim());
    }

    if (a.street) {
        const stType = a.streetType ? normalizeStreetType(a.streetType) : '';
        const streetFormatted = stType ? (new RegExp(`^${stType}\\b`, 'i').test(a.street) ? a.street : `${stType} ${a.street}`.trim()) : a.street;
        parts.push(streetFormatted);
    }

    if (a.house) parts.push(`д. ${a.house}`);
    if (a.block) parts.push(`к. ${a.block}`);
    if (a.flat) parts.push(`кв. ${a.flat}`);

    return parts.join(', ');
}

export function shallowAddressFromLegacy(raw: string): Partial<AddressAny> {
    if (!raw) return {};
    const postal = raw.match(/(^|\s)(\d{6})(?=,|\s|$)/)?.[2] || '';
    const cityMatch = raw.match(/г\.\s*([А-ЯA-Za-zЁё\-\s]+)/);
    const streetMatch = raw.match(/(ул\.?|улица|пр-кт|проспект|пер\.?|переулок|бульвар|бул\.?|проезд|пл\.?|площадь|наб\.?|набережная)\s*([А-ЯA-Za-z0-9Ёё\-\s]+)/i);
    const houseMatch = raw.match(/д\.?\s*([0-9A-Za-zА-Яа-я/-]+)/);
    const blockMatch = raw.match(/к\.?\s*([0-9A-Za-zА-Яа-я]+)/);
    const flatMatch = raw.match(/кв\.?\s*([0-9A-Za-z]+)/);

    let streetType = streetMatch?.[1] || '';
    streetType = normalizeStreetType(streetType);

    return {
        postalCode: postal,
        city: cityMatch ? cityMatch[1].trim() : '',
        cityType: cityMatch ? 'г' : '',
        street: streetMatch ? streetMatch[2].trim() : '',
        streetType,
        house: houseMatch?.[1] || '',
        block: blockMatch?.[1] || '',
        flat: flatMatch?.[1] || '',
    };
}

export function maskDobInput(value: string): string {
    const digits = (value || '').replace(/\D/g, '').slice(0, 8); // DDMMYYYY
    const d = digits.slice(0, 2);
    const m = digits.slice(2, 4);
    const y = digits.slice(4, 8);
    let out = d;
    if (digits.length > 2) out += `.${m}`;
    if (digits.length > 4) out += `.${y}`;
    return out;
}

export function normalizeDobFromServer(raw: string): string {
    const v = (raw || '').trim();
    if (!v) return '';
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(v)) return v;
    const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}.${m[2]}.${m[1]}`;
    return maskDobInput(v);
}

export function getDobError(value: string): string | null {
    const v = (value || '').trim();
    if (!v) return 'укажите дату рождения';
    if (!/^\d{2}\.\d{2}\.\d{4}$/.test(v)) return 'формат: ДД.ММ.ГГГГ';

    const [ddStr, mmStr, yyyyStr] = v.split('.');
    const dd = Number(ddStr);
    const mm = Number(mmStr);
    const yyyy = Number(yyyyStr);

    if (!Number.isFinite(dd) || !Number.isFinite(mm) || !Number.isFinite(yyyy)) return 'некорректная дата';
    if (yyyy < 1900) return 'слишком ранний год';
    if (mm < 1 || mm > 12) return 'месяц должен быть 01..12';
    if (dd < 1 || dd > 31) return 'день должен быть 01..31';

    const dt = new Date(yyyy, mm - 1, dd);
    if (dt.getFullYear() !== yyyy || dt.getMonth() !== mm - 1 || dt.getDate() !== dd) return 'такой даты не существует';

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dt.setHours(0, 0, 0, 0);

    if (dt > today) return 'некорректный возраст';

    const maxAgeYears = 120;
    const minAgeYears = 12;

    const minAllowed = new Date(today);
    minAllowed.setFullYear(today.getFullYear() - maxAgeYears);
    if (dt < minAllowed) return 'некорректный возраст';

    const maxAllowed = new Date(today);
    maxAllowed.setFullYear(today.getFullYear() - minAgeYears);
    if (dt > maxAllowed) return 'некорректный возраст';

    return null;
}

/**
 * Нормализация адреса пользователя:
 * user.address -> null | { raw: string, struct: AddressStruct }
 * + чистим legacy поля addressStruct/addressRaw.
 */
export function normalizeUserAddress(u: any): any {
    let changed = false;
    const next = { ...u };

    const hasContainer = next.address && typeof next.address === 'object' && !Array.isArray(next.address);
    const containerRaw = hasContainer ? next.address.raw : undefined;
    const containerStruct = hasContainer ? next.address.struct : undefined;

    if (hasContainer && containerRaw && containerStruct) {
        if (next.addressStruct || next.addressRaw) {
            delete next.addressStruct;
            delete next.addressRaw;
            changed = true;
        }
        return changed ? next : u;
    }

    if (hasContainer) {
        if (containerStruct && !containerRaw) {
            const raw = buildFullAddress(containerStruct);
            next.address = raw ? { raw, struct: containerStruct } : null;
            changed = true;
        } else if (containerRaw && !containerStruct) {
            const parsed = shallowAddressFromLegacy(containerRaw);
            const struct = { ...emptyAddress, ...parsed };
            const rawBuilt = buildFullAddress(struct);
            next.address = rawBuilt ? { raw: rawBuilt, struct } : null;
            changed = true;
        } else if (!containerRaw && !containerStruct) {
            next.address = null;
            changed = true;
        }
    } else {
        if (next.addressStruct) {
            const raw = buildFullAddress(next.addressStruct);
            next.address = raw ? { raw, struct: next.addressStruct } : null;
            changed = true;
        } else if (typeof next.address === 'string') {
            const parsed = shallowAddressFromLegacy(next.address);
            const struct = { ...emptyAddress, ...parsed };
            const rawBuilt = buildFullAddress(struct);
            next.address = rawBuilt ? { raw: rawBuilt, struct } : null;
            changed = true;
        } else if (!next.address) {
            next.address = null;
        }
    }

    if (next.addressStruct) {
        delete next.addressStruct;
        changed = true;
    }
    if (next.addressRaw) {
        delete next.addressRaw;
        changed = true;
    }
    return changed ? next : u;
}

export function debounce<T extends (...args: any[]) => void>(fn: T, ms: number) {
    let t: ReturnType<typeof setTimeout> | null = null;
    return (...args: Parameters<T>) => {
        if (t) clearTimeout(t);
        t = setTimeout(() => fn(...args), ms);
    };
}
